import React, { useEffect, useMemo, useState } from "react";
import "./SpecialGrid.css";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../../Features/Cart/cartSlice";
import StoreData from "../../Data/StoreData";
import { FaCartPlus } from "react-icons/fa";
import toast from "react-hot-toast";
import { Pagination } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import paymentImage from "../../Assets/Payment/paywithplux.png";
import { RootState, AppDispatch } from "../../App/store";
import { fetchStockItems } from "../../Action";
import { IStockItem } from "../../Interface/Product.interface";
import usePagination from "../../Pagination/Usepagination";
import ProductSkeleton from "../Skeleton/Skeletonmui";

const SpecialGridComponent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [sortType, setSortType] = useState<string>("default");
  const [viewType, setViewType] = useState<"grid" | "list">("grid");
  const [filteredItems, setFilteredItems] = useState<IStockItem[]>([]);
  const [labels, setLabels] = useState<string>("All Products");
  const [searchTerm, setSearchTerm] = useState("");

  const cartItems = useSelector((state: RootState) => state.cart.items);
  const { stockItems, loading, error } = useSelector((state: RootState) => state.pos);

  const location = useLocation();
  const navigate = useNavigate();

  // Sorting the filtered items
  const sortedItems = useMemo(() => {
    const itemsCopy = [...filteredItems];

    switch (sortType) {
      case "name":
        return itemsCopy.sort((a, b) => a.itemname.localeCompare(b.itemname));
      case "price":
        return itemsCopy.sort((a, b) => a.BTCSellingRate - b.BTCSellingRate);
      default:
        return itemsCopy;
    }
  }, [filteredItems, sortType]);

  // Pagination hook
  const { currentItems, currentPage, totalPages, setCurrentPage } = usePagination<IStockItem>(sortedItems, 16);

  // Fetch stock items on mount
  useEffect(() => {
    dispatch(fetchStockItems());
  }, [dispatch]);

  // Filter products based on URL params (subLabel/category)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subLabel = params.get("subLabel")?.toLowerCase() || "";
    const category = params.get("category")?.toLowerCase() || "";

    if (stockItems.length === 0) return;

    // Default: show all products
    let filtered: IStockItem[] = stockItems;
    let label = "All Categories";

    if (category && category !== "all categories") {
      filtered = stockItems.filter((item: IStockItem) =>
        item.category?.toLowerCase().includes(category)
      );
      label = category.charAt(0).toUpperCase() + category.slice(1); // Capitalize label
    } else if (subLabel) {
      filtered = stockItems.filter((item: IStockItem) =>
        item.subcategoryname?.toLowerCase().includes(subLabel)
      );
      label = subLabel.charAt(0).toUpperCase() + subLabel.slice(1);
    }

    setFilteredItems(filtered);
    setLabels(label);
  }, [stockItems, location.key]);




  // Search handlers
  const handleSearch = () => {

    if (searchTerm.trim() === "") return;

    const matched = stockItems.filter(
      (item: IStockItem) =>
        item.itemname?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredItems(matched);
    setLabels(`Results for "${searchTerm}"`);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm(""); const params = new URLSearchParams(location.search);
    const subLabel = params.get("subLabel")?.toLowerCase() || "";
    const category = params.get("category")?.toLowerCase() || "";

    if (stockItems.length === 0) return;

    // Default: show all products
    let filtered: IStockItem[] = stockItems;
    let label = "All Categories";

    if (category && category !== "all categories") {
      filtered = stockItems.filter((item: IStockItem) =>
        item.category?.toLowerCase().includes(category)
      );
      label = category.charAt(0).toUpperCase() + category.slice(1); // Capitalize label
    } else if (subLabel) {
      filtered = stockItems.filter((item: IStockItem) =>
        item.subcategoryname?.toLowerCase().includes(subLabel)
      );
      label = subLabel.charAt(0).toUpperCase() + subLabel.slice(1);
    }

    setFilteredItems(filtered);
    setLabels(label);
  };

  // Navigation to product page
  const handleOnNavigation = (product: IStockItem) => {
    navigate(`/product?id=${product.itemid}`, { state: product });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim() === "") {
      // Clear filtered data if input is empty or just spaces
      handleClearSearch()
    }
  };

  return (
    <div className="specialGrid-container">
      <div className="specialGrid-header">
        <h3>Pay with</h3>
        <img src={paymentImage} alt="Payment" height={50} />
      </div>

      <h2 className="specialGrid-title">{labels}</h2>

      {/* Search Bar */}
      <div className="specialGrid-search">
        <input
          type="text"
          placeholder="Search by product name..."
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
        />
        <button onClick={handleSearch}>Search</button>
        <button onClick={handleClearSearch}>Clear</button>
      </div>

      {/* Sort Controls */}
      <div className="specialGrid-controls">
        <div className="specialGrid-sort">
          <label htmlFor="sortSelect">Sort By:</label>
          <select
            id="sortSelect"
            value={sortType}
            onChange={(e) => setSortType(e.target.value)}
          >
            <option value="default">Default</option>
            <option value="name">Name</option>
            <option value="price">Price</option>
          </select>
        </div>

        {/* View Toggle (commented out) */}
        {/* <div className="specialGrid-toggle">
          <button
            className={viewType === "grid" ? "active" : ""}
            onClick={() => setViewType("grid")}
          >
            🔳 Grid
          </button>
          <button
            className={viewType === "list" ? "active" : ""}
            onClick={() => setViewType("list")}
          >
            📋 List
          </button>
        </div> */}
      </div>

      {/* Product Grid/List */}
      <div
        className={`specialGrid-main ${viewType === "list" ? "specialGrid-list-view" : ""
          }`}
      >
        {loading ? (
          <ProductSkeleton count={4} />
        ) : currentItems.length > 0 ? (
          currentItems.map((product, index) => (
            <div className="specialGrid-card" key={`${product.itemid}-${index}`}>
              <div className="specialGrid-image-box">
                <div className="specialGrid-sale-badge">Sale</div>
                <img
                  src={`data:image/jpeg;base64,${product.FilePath}`}
                  alt={product?.itemname}
                />
                <button
                  className="specialGrid-view-btn"
                  onClick={() => handleOnNavigation(product)}
                >
                  View
                </button>
              </div>

              <div className="specialGrid-info">
                <div className="specialGrid-meta">
                  <p>{product?.itemname}</p>
                </div>
                <div className="specialGrid-name-price">
                  <div className="specialGrid-price-cart-row">
                    <div className="specialGrid-price-discount-wrapper">
                      <p className="specialGrid-price">
                        ₹{product.BTCSellingRate} Inc Tax
                      </p>
                      <p className="specialGrid-discount">
                        ₹{product.Mrprate} Inc Tax
                      </p>
                    </div>
                    <div
                      className="specialGrid-cart-inline"
                      onClick={() => handleOnNavigation(product)}
                      title="Add to Cart"
                    >
                      <FaCartPlus />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div>
            No data found in{" "}
            <strong>{labels ? labels : "ProductList"}</strong>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="specialGrid-pagination">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(_, value) => setCurrentPage(value)}
          color="primary"
          shape="rounded"
          sx={{ mt: 2 }}
        />
      </div>
    </div>
  );
};

export default SpecialGridComponent;
